import { useState, type FormEvent } from 'react'
import { ADDRESS_UI, type AddressDraft } from '../constants/addresses'
import { toE164Phone } from '../helpers/auth'
import { nationalPhoneDigits } from '../helpers/orders'
import { useAuth } from './useAuth'
import { useSavedAddresses } from './useSavedAddresses'

const EMPTY_DRAFT: AddressDraft = {
  recipientName: '',
  recipientPhone: '',
  address: '',
  city: '',
  pincode: '',
}

export function useAddressesPage() {
  const { user } = useAuth()
  const { addresses, loading, save, remove } = useSavedAddresses()
  const [draft, setDraft] = useState<AddressDraft>(() => ({
    ...EMPTY_DRAFT,
    recipientName: user?.name?.trim() ?? '',
    recipientPhone: nationalPhoneDigits(user?.phone),
  }))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirmingId, setConfirmingId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  function updateDraft(patch: Partial<AddressDraft>) {
    setDraft((current) => ({ ...current, ...patch }))
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    if (
      !draft.recipientName.trim() ||
      nationalPhoneDigits(draft.recipientPhone).length !== 10 ||
      !draft.address.trim() ||
      !draft.city.trim() ||
      !draft.pincode.trim()
    ) {
      setError(ADDRESS_UI.saveError)
      return
    }
    setSaving(true)
    setError(null)
    try {
      await save({
        recipientName: draft.recipientName.trim(),
        recipientPhone: toE164Phone(draft.recipientPhone),
        address: draft.address.trim(),
        city: draft.city.trim(),
        pincode: draft.pincode.trim(),
      })
      setDraft({
        ...EMPTY_DRAFT,
        recipientName: user?.name?.trim() ?? '',
        recipientPhone: nationalPhoneDigits(user?.phone),
      })
    } catch {
      setError(ADDRESS_UI.saveError)
    } finally {
      setSaving(false)
    }
  }

  function askDelete(id: string) {
    setDeleteError(null)
    setConfirmingId(id)
  }

  function cancelDelete() {
    setDeleteError(null)
    setConfirmingId(null)
  }

  async function confirmDelete(id: string) {
    if (deletingId) return
    setDeletingId(id)
    setDeleteError(null)
    try {
      await remove(id)
      setConfirmingId(null)
    } catch {
      setDeleteError(ADDRESS_UI.deleteError)
    } finally {
      setDeletingId(null)
    }
  }

  return {
    addresses,
    loading,
    draft,
    updateDraft,
    saving,
    error,
    onSubmit,
    confirmingId,
    deletingId,
    deleteError,
    askDelete,
    cancelDelete,
    confirmDelete,
  }
}
